import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Button, Checkbox, Dialog, DialogActions, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getAccountsInformation, setPreferences } from '../../../../services/manage/accounts';
import { getWebhookStatus, requestWebhook, updateWebhook, updateWebhookSubscription } from '../../../../services/manage/settings';
import { RootState } from '../../../../store';
import { setUserPreferences } from '../../../../store/main';
import { FormState } from '../../../../type/user';
import { AppURL } from '../../../../utility/AppURL';
import ContentLayout from '../../../Layout/Content';
import { showToast } from '../../../utils/notifications';
import Preferences from './preferences';

interface WebhookInterface {
    type: string;
    url: string;
    triggerList: string[];
}

let PlainOptions = [
    {
        label: 'Notify when AppSealing`s new announcement arrived',
        value: 'ADC_NEW_ANNOUNCE',
    },
    {
        label: 'Notify when AppSealing`s new Invoice is sent',
        value: 'INVOICE_SENT',
    },
    {
        label: 'Notify when AppSealing update new version for Android',
        value: 'AOS_NATIVE_UPDATED',
    },
    {
        label: 'Notify when AppSealing update new version for iOS',
        value: 'AOS_HYBRID_UPDATED',
    },
];

const GeneralSettings = () => {
    const { i18n, t } = useTranslation();
    const dispatch = useDispatch();
    const [showMask, setShowMask] = useState<{ msg: string } | null>(null);
    const { authContext } = useSelector((state: RootState) => state.mainReducer);
    const userPreferences = useSelector((state: RootState) => state.mainReducer.userPreferences);
    const userDetails = useSelector((state: RootState) => state.mainReducer.userDetails);
    
    const [feedback, setFeedback] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [webhookData, setWebhookData] = useState<WebhookInterface | undefined>();
    const [checkedValues, setCheckedValues] = useState<string[]>([]);
    const [disabled, setDisabled] = useState(true);
    
    const defaultFormValues = {
        preferences: {
            lang_set: userPreferences.lang_set,
            timezone_code: userPreferences.timezone_code,
            view_sign_yn: userPreferences.view_sign_yn,
        },
    };

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty },
        getValues,
        setValue,
    } = useForm<FormState>({
        defaultValues: defaultFormValues,
    });

    useEffect(() => {
        if (userPreferences) {
            const lang = userPreferences.lang_set;
            setValue('preferences.lang_set', lang);
        }
    }, [i18n, setValue, userPreferences]);

    useEffect(() => {
        const fetchAccountInfo = async () => {
            if (authContext?.token) {
                const response = await getAccountsInformation({
                    token: authContext.token,
                });
                if (response?.result.code === '0000') {
                    dispatch(setUserPreferences(response.preference));
                    reset({
                        preferences: response.preference,
                    });
                }
            }
        };
        fetchAccountInfo();
    }, [authContext, dispatch, reset]);

    useEffect(() => {
        if (!webhookData && authContext?.token) {
            const getWebhookDetails = async () => {
                const params = { token: authContext.token };
                const response = await getWebhookStatus(params);

                var data: WebhookInterface = {
                    type: 'SLACK',
                    url: '',
                    triggerList: PlainOptions.map((e: any) => e.value),
                };

                if (response?.result?.code === '0000' && response?.webhookUrl?.webhook_url) {
                    data.url = response.webhookUrl.webhook_url;
                    data.type = response.webhookUrl.webhook_type;
                    data.triggerList = [];
                    response.subscribedTriggerList.forEach((e: any) => {
                        if (e.enabled === 'Y') data.triggerList.push(e.trigger_code);
                    });
                }
                setWebhookData(data);
                setCheckedValues(data.triggerList);
                setDisabled(false);
            };
            getWebhookDetails();
        }
    }, [webhookData, authContext?.token]);

    const onPlatformChange = (e: { target: { value: any } }) => {
        let value = e.target.value;
        if (value === 'other') setIsModalVisible(true);
        else {
            setWebhookData((oldData) => {
                let data;
                if (oldData) data = { ...oldData, type: value };
                return data;
            });
        }
    };

    const onURLChange = (e: any) => {
        setWebhookData((oldData) => {
            let data;
            if (oldData) data = { ...oldData, url: e.target.value };
            return data;
        });
    };

    const onCheckAllChange = (e: any) => {
        const triggerList = e.target.checked ? PlainOptions.map((e: any) => e.value) : [];
        setWebhookData((oldData) => {
            let data;
            if (oldData) data = { ...oldData, triggerList };
            return data;
        });
        setCheckedValues(triggerList);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setCheckedValues((prevValues) => (prevValues.includes(value) ? prevValues.filter((v) => v !== value) : [...prevValues, value]));
        setWebhookData((oldData) => {
            if (oldData) {
                const newTriggerList = checkedValues.includes(value) 
                    ? checkedValues.filter((v) => v !== value) 
                    : [...checkedValues, value];
                return { ...oldData, triggerList: newTriggerList };
            }
            return oldData;
        });
    };

    const onSendClick = async () => {
        const payload = `${t('webhook_request_arrived')}\n
        >>> ${userDetails.user_id} \n
        ${t('at')} = ${window.location.href} \n
        ${t('feedback')}:\n ${feedback}`;

        const response = await requestWebhook({ payload });
        if (response?.result?.code === '0000') {
            setFeedback('');
            setIsModalVisible(false);
            showToast('success', t('your_request_for_webhook_sent_successfully_contact_help_desk_for_any_assistance'));
        } else if (response?.result?.code) {
            showToast('error', t(`RCM-${response?.result?.code}`, t('error_while_sending_feedback_please_try_again_or_connect_with_help_desk')));
        } else {
            showToast('error', t('error_while_sending_feedback_please_try_again_or_connect_with_help_desk'));
        }
    };
   
    const onSubmit = async () => {
        setShowMask({ msg: t('updating_user_profile') });

        const preferences = {
            lang_set: getValues('preferences.lang_set'),
            timezone_code: getValues('preferences.timezone_code'),
            view_sign_yn: getValues('preferences.view_sign_yn'),
        };
        
        let successFlag = false;
        let errorShown = false;

        try {
            const tasks = [];
            let successCount = 0;
            const isPreferencesChanged = JSON.stringify(preferences) !== JSON.stringify(defaultFormValues.preferences);

            if (isPreferencesChanged) {
                let updatePreferences = { ...preferences };
                tasks.push(setPreferences({ ...updatePreferences, token: authContext.token }));
            }
            if (webhookData) {
                const webhookParams = {
                    token: authContext.token,
                    webhook_type: webhookData.type || 'SLACK',
                    webhook_url: webhookData.url || '',
                };
                tasks.push(updateWebhook(webhookParams));

                const subscriptionParams = {
                    token: authContext.token,
                    options: PlainOptions.map((e) => e.value),
                    triggers: webhookData.triggerList || [],
                };
                tasks.push(updateWebhookSubscription(subscriptionParams));
            }

            const results = await Promise.allSettled(tasks);
            results.forEach((result) => {
                if (result.status === 'fulfilled' && result.value && result.value.result) {
                    if (result.value.result.code === '0000' || result.value.code === '0000') {
                        successCount++;
                        if (!successFlag) {
                            if (isPreferencesChanged) {
                                const lang = preferences.lang_set;
                                localStorage.setItem('user-lang', lang === 'KR' ? 'kr_KR' : 'en_US');
                                i18n.changeLanguage(lang === 'KR' ? 'kr_KR' : 'en_US');
                            }

                            reset(
                                {
                                    ...getValues(),
                                    password: { user_pw: '', new_user_pw: '', new_user_pw_confirm: '' },
                                },
                                {
                                    keepErrors: false,
                                    keepDirty: false,
                                    keepIsSubmitted: false,
                                    keepTouched: false,
                                    keepIsValid: false,
                                    keepSubmitCount: false,
                                },
                            );
                        }
                    } else if (!errorShown) {
                        showToast('error', t(`RCM-${result.value.result.code}`, result.value.result.message as string));
                        errorShown = true;
                    }
                } else if (!errorShown && result.status === 'rejected' && result.reason && result.reason.result && result.reason.result.code) {
                    showToast('error', t(`RCM-${result.reason.result.code}`, result.reason.result.message as string));
                    errorShown = true;
                }

                if (!errorShown && successCount === tasks.length) {
                    showToast('success', t('profile_information_updated_successfully'));
                    successFlag = true;
                }
            });

            setShowMask(null);
        } catch (error) {
            if (!errorShown) {
                showToast('error', t('failed_to_update_profile_information'));
            }
            setShowMask(null);
        }
    };

    const checkboxes = PlainOptions.map((option) => (
        <FormControlLabel
            key={option.value}
            control={<Checkbox size={'small'} checked={checkedValues.includes(option.value)} onChange={handleCheckboxChange} value={option.value} />}
            label={<span className="text-darkgray text-sm">{t(option.label.toLowerCase().replaceAll(' ', '_'))}</span>}
        />
    ));

    const hasChanges = isDirty || (webhookData && (webhookData.url || webhookData.triggerList.length > 0));

    return (
        <ContentLayout 
            showMask={showMask} 
            title={t('general_settings')} 
            onClick={handleSubmit(onSubmit)} 
            disabled={!hasChanges} 
            btnText={t('save')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Preferences control={control} />
                <hr className="mt-7 mb-10" />
                <Box mt={4}>
                    <div className="text-base font-bold">{t('manage_product_notification')}</div>
                    <div className="mx-2.5">
                        <div className="my-5">
                            <span className="text-darkgrey text-sm">{t('get_notified_about_appsealing_updates_directly_via_your_communication_platform_tools')}</span>
                            <Tooltip
                                placement="top"
                                title={
                                    <p>
                                        <span>{t('do_you_need_a_guide_to_create_slack_webhook_url')}</span>
                                        <a className="ml-2 underline text-white" target="_blank" rel="noreferrer" href={AppURL.slackwebhook}>
                                            {t('click_here')}
                                        </a>
                                    </p>
                                }
                            >
                                <HelpOutlineOutlinedIcon className="ml-2 mt-auto mb-auto align-middle text-sm text-darkgray" />
                            </Tooltip>
                        </div>
                        <div className="flex flex-none gap-4">
                            <FormControl fullWidth className="sm:w-52">
                                <InputLabel>{t('select_platform')}</InputLabel>
                                <Select 
                                    value={webhookData?.type || 'SLACK'} 
                                    onChange={onPlatformChange} 
                                    size={'small'} 
                                    label={t('select_platform')}
                                    disabled={disabled}
                                >
                                    <MenuItem value="SLACK">{t('slack_webhook')}</MenuItem>
                                    <MenuItem value="other">{t('other_platform')}</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                type="url"
                                placeholder="https://...."
                                value={webhookData?.url || ''}
                                fullWidth
                                className="max-w-[300px]"
                                label={t('site_address')}
                                size={'small'}
                                onChange={onURLChange}
                                variant="outlined"
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    <Box className="mt-10">
                        <p className="text-base font-bold mb-5">{t('list_of_appsealing_notification_events')} :</p>
                        <div className="pl-7">
                            <FormGroup>
                                <FormControlLabel
                                    key={'select_all'}
                                    control={
                                        <Checkbox 
                                            size="small" 
                                            checked={checkedValues.length > 0 && checkedValues.length > 3} 
                                            onChange={onCheckAllChange}
                                            disabled={disabled} 
                                        />
                                    }
                                    label={<span className="font-bold text-darkgray text-sm">{t('select_all_notification_events')}</span>}
                                />
                                {checkboxes}
                            </FormGroup>
                        </div>
                    </Box>
                </Box>
            </form>
            <Dialog open={isModalVisible} onClose={() => setIsModalVisible(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className="modalStyle">
                    <Typography variant="h1">
                        {t('request_other_platform_integration')}
                    </Typography>
                    <Box className="flex flex-wrap gap-4">
                        <Typography mt={2} mb={1} className='text-sm'>
                            {t('request_other_platform_integration_msg')}
                        </Typography>
                        <TextField
                            placeholder={t('please_specify_other_platform_details')}
                            multiline
                            minRows={2}
                            maxRows={3}
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            style={{ width: '100%' }}
                            variant="outlined"
                        />
                    </Box>
                    <DialogActions className='mt-10'>
                        <Button variant='contained' onClick={onSendClick}>{t('send')}</Button>
                        <Button variant="outlined" color="secondary" onClick={() => setIsModalVisible(false)}>{t('cancel')}</Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </ContentLayout>
    );
};

export default GeneralSettings;